/* SPEC-145 — the floating narration card.
 *
 * Positions itself next to the spotlighted rect (below it by default, flipping
 * above / centering when there isn't room), traps focus while open, and reads
 * the narration into an aria-live region. Keyboard (←/→/Esc) is handled by the
 * provider at the document level; the on-card buttons mirror it for the mouse. */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { TourStep } from '@/config/tours';
import { computeCardPosition } from './placement';

interface TourCardProps {
  step: TourStep;
  index: number;
  total: number;
  tourTitle: string;
  rect: DOMRect | null;
  onNext: () => void;
  onBack: () => void;
  onExit: () => void;
}

/** Place the card near the target rect, always fully within the live viewport.
 *  Geometry lives in ./placement (pure + unit-tested); this wrapper just feeds
 *  it the current window size. */
function computePosition(rect: DOMRect | null, cardHeight: number) {
  return computeCardPosition(rect, cardHeight, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
}

export function TourCard({
  step, index, total, tourTitle, rect, onNext, onBack, onExit,
}: TourCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>(
    () => computePosition(rect, 220),
  );

  const isLast = index >= total - 1;
  const isFirst = index <= 0;

  // Reposition after render (we now know the card's real height) and on the rect
  // moving / the window resizing.
  useLayoutEffect(() => {
    const reposition = () => {
      const h = cardRef.current?.offsetHeight ?? 220;
      setPos(computePosition(rect, h));
    };
    reposition();
    window.addEventListener('resize', reposition);
    return () => window.removeEventListener('resize', reposition);
  }, [rect, step]);

  // Move keyboard focus onto the primary action when the step changes, so the
  // narration is reachable and Enter advances.
  useEffect(() => {
    nextRef.current?.focus();
  }, [index]);

  // Focus trap: keep Tab inside the card while the tour is open.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = cardRef.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={cardRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-card-title"
      aria-describedby="tour-card-body"
      onKeyDown={onKeyDown}
      className="fixed z-[70] rounded-xl border border-amber-500/40 bg-[var(--bg-raised)] shadow-2xl p-4"
      style={{ top: pos.top, left: pos.left, width: pos.width }}
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-400/90">
            {tourTitle}
          </p>
          <h2 id="tour-card-title" className="text-sm font-display font-bold text-[var(--text-primary)] leading-snug mt-0.5">
            {step.title}
          </h2>
        </div>
        <button
          onClick={onExit}
          className="p-1 -mr-1 -mt-1 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] transition-colors shrink-0"
          title="Exit walk-through (Esc)"
          aria-label="Exit walk-through"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p
        id="tour-card-body"
        aria-live="polite"
        className="text-[13px] leading-relaxed text-[var(--text-secondary)]"
      >
        {step.body}
      </p>

      <div className="flex items-center justify-between gap-2 mt-4">
        <span className="text-[11px] font-mono text-[var(--text-tertiary)] tabular-nums">
          {index + 1} / {total}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onBack}
            disabled={isFirst}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-overlay)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <button
            ref={nextRef}
            onClick={onNext}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-[var(--text-inverse)] hover:bg-amber-600 transition-colors"
          >
            {isLast ? 'Done' : 'Next'}
            {!isLast && <ArrowRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
